import React, { useCallback, useState } from "react";
import { Box, Button, ButtonGroup, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { i18n } from "../../translate/i18n";
import { useDispatch, useSelector } from "react-redux";
import * as actions from '../../store/actions';
import { formatLanguageFromLocalStorage } from "../../utils/formatLanguageFromLocalStorage";
import moment from "moment";

type Props = {
  closeModal: () => void;
}

type ButtonProps = {
  onClick: () => void;
  selected: boolean,
  label: string,
};

enum EExportType {
  excel = 'excel',
  pdf = 'pdf',
}

enum EExportDataType {
  actions = 'actions',
  services = 'services',
  servicesReplicated = 'servicesReplicated',
  maintenances = 'maintenances',
}

const ExportModalCalendar: React.FC<Props> = ({ closeModal }) => {
  const dispatch = useDispatch();

  const filteredUsers = useSelector((state: any) => state.calendars.filteredUsers);
  const filteredUsersMaintenances = useSelector((state: any) => state.calendars.filteredUsersMaintenances);
  const filteredEquipmentModels = useSelector((state: any) => state.calendars.filteredEquipmentModels);
  const filteredEquipmentGroups = useSelector((state: any) => state.calendars.filteredEquipmentGroups);
  const filteredServiceTypes = useSelector((state: any) => state.calendars.filteredServiceTypes);
  const filteredFrequencies = useSelector((state: any) => state.calendars.filteredFrequencies);
  const filteredPlaces = useSelector((state: any) => state.calendars.filteredPlaces);
  const filteredAreas = useSelector((state: any) => state.calendars.filteredAreas);
  const filteredStatus = useSelector((state: any) => state.calendars.filteredStatus);
  const filteredStatusMaintenances = useSelector((state: any) => state.calendars.filteredStatusMaintenances);
  const filteredCompanies = useSelector((state: any) => state.calendars.filteredCompanies);
  const loadingPdfExport = useSelector((state: any) => state.calendars.loadingPdfExport);
  const selectedSites = useSelector((state: any) => state.sites.selectedSitesPlaces);
  const calendarDate = useSelector((state: any) => state.calendars.calendarDate);

  const localStorageLang = localStorage.getItem("i18nextLng");
  const emailLanguage = formatLanguageFromLocalStorage(localStorageLang);

  const start = moment(calendarDate).startOf('month');
  const end = moment(calendarDate).endOf('month');

  const fetchCalendarsExport = useCallback(async (modules: string[]) => dispatch(actions.fetchCalendarsExport(start, end, selectedSites, filteredUsers, filteredUsersMaintenances, filteredEquipmentModels, filteredEquipmentGroups, filteredServiceTypes, filteredFrequencies, filteredPlaces, modules, filteredStatus, filteredStatusMaintenances, filteredAreas, filteredCompanies, emailLanguage)) as any, [calendarDate, selectedSites, filteredUsers, filteredUsersMaintenances, filteredEquipmentModels, filteredEquipmentGroups, filteredServiceTypes, filteredFrequencies, filteredPlaces, filteredStatus, filteredStatusMaintenances, filteredAreas, filteredCompanies, emailLanguage]);

  const fetchCalendarsPdfExport = useCallback(async (modules: string[]) => dispatch(actions.fetchCalendarsPdfExport(start, end, selectedSites, filteredUsers, filteredUsersMaintenances, filteredEquipmentModels, filteredEquipmentGroups, modules, filteredAreas, filteredCompanies, filteredPlaces, filteredServiceTypes, filteredFrequencies,filteredStatus, filteredStatusMaintenances, emailLanguage)) as any, [calendarDate, selectedSites, filteredUsers, filteredUsersMaintenances, filteredEquipmentModels, filteredEquipmentGroups, filteredServiceTypes, filteredFrequencies, filteredPlaces, filteredStatus, filteredStatusMaintenances, filteredAreas, filteredCompanies, emailLanguage]);

  const [exportType, setExportType] = useState<EExportType>(EExportType.excel);
  const [exportDataType, setExportDataType] = useState<EExportDataType>(EExportDataType.actions);

  const TypeButton: React.FC<ButtonProps> = ({ selected, onClick, label }) => {
    const variant = selected ? 'contained' : 'outlined';
    return (
      <Button sx={{boxShadow: 'none'}} variant={variant} onClick={onClick}>{label}</Button>
    )
  }

  const handleSubmit = async () => {
    let response = '';
    if (exportType === EExportType.excel) {
      response = await fetchCalendarsExport([exportDataType]);
    } else if (exportType === EExportType.pdf) {
      response = await fetchCalendarsPdfExport([exportDataType]);
    }
    // if (response === 'success') closeModal();
  }
  
  return (
    <Box sx={{width: '560px', display: 'flex', flexDirection: 'column'}}>
      <Typography
        sx={{
          fontSize: '20px',
          lineHeight: '32px',
          fontWeight: 500,
          paddingX: '24px',
          paddingY: '16px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}
      >
        {i18n.t('exportModalCalendar.exportData') as string} 
      </Typography>
      <Box sx={{ paddingX: '24px', paddingY: '16px', borderBottom: '1px solid rgba(0, 0, 0, 0.12)'}}>
        <ButtonGroup sx={{marginBottom: '24px'}}>
          <TypeButton selected={exportType === EExportType.excel} onClick={() => setExportType(EExportType.excel)} label={i18n.t('exportModalCalendar.excel') as string}/>
          <TypeButton selected={exportType === EExportType.pdf} onClick={() => setExportType(EExportType.pdf)} label='PDF'/>
        </ButtonGroup>
        <RadioGroup value={exportDataType} onChange={(e) => setExportDataType(e.target.value as EExportDataType)}>
          <FormControlLabel value={EExportDataType.actions} control={<Radio />} label={i18n.t('formControlLabelCalendar.checkActions') as string} />
          <FormControlLabel value={EExportDataType.services} control={<Radio />} label={i18n.t('formControlLabelCalendar.checkServices') as string} />
          <FormControlLabel value={EExportDataType.servicesReplicated} control={<Radio />} label={i18n.t('formControlLabelCalendar.checkServicesReplicated') as string} />
          <FormControlLabel value={EExportDataType.maintenances} control={<Radio />} label={i18n.t('formControlLabelCalendar.checkMaintenances') as string} />
        </RadioGroup>
      </Box>
      <Box sx={{padding: '16px 24px', alignSelf: 'flex-end'}}>
        <Button onClick={() => closeModal()} sx={{color: 'red', marginRight: '10px', fontWeight: 500}}>{i18n.t('scheduleServiceModal.cancel') as string}</Button>
        <LoadingButton loading={loadingPdfExport} onClick={handleSubmit} variant="contained" color='primary'>{i18n.t('exportModalCalendar.exportData') as string}</LoadingButton>
      </Box>
    </Box>
  );
};

export default ExportModalCalendar;